import * as ngCore from '@angular/core'

/**
 * Boundary tokens for component classes that the state layer needs at runtime.
 *
 * The state layer must NOT import from `../components` directly because barrel
 * re-exports pull in the entire component tree — including heavy dependencies
 * such as WebGL / sigma — which breaks unit tests that run in jsdom.
 *
 * Instead, declare an {@link ngCore.InjectionToken} here and add a
 * corresponding entry in the sibling {@link ./providers.ts providers} file.
 * TypeScript enforces that every token in {@link TOKENS} has a matching
 * provider — a missing entry causes a compile error.
 *
 * @example Adding a new bridged component
 * ```
 * // 1. tokens.ts — declare the token and add it to TOKENS
 * export const MY_COMPONENT = new ngCore.InjectionToken<ngCore.Type<unknown>>(
 *   'componentBridge.MyComponent',
 * )
 * export const TOKENS = { ..., MY_COMPONENT } as const
 *
 * // 2. providers.ts — add the mapping (TS errors until you do)
 * const COMPONENT_MAP = { ..., MY_COMPONENT: components.x.y.Component } satisfies ...
 * ```
 */

/** Readonly reference-value renderer used in DMS table cells */
export const REFERENCE_CELL_COMPONENT = new ngCore.InjectionToken<
  ngCore.Type<unknown>
>('componentBridge.ReferenceCellComponent')

/**
 * All bridge tokens. Used by {@link ./providers.ts} to enforce at compile time
 * that every token has a corresponding component mapping.
 */
export const TOKENS = {
  referenceCellComponent: REFERENCE_CELL_COMPONENT,
} as const
